import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, Calendar, Gift } from 'lucide-react';
import OrderTrackingTimeline from '../components/OrderTrackingTimeline';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import NoticeBanner from '@/components/NoticeBanner';
import LoyaltyCard from '@/components/LoyaltyCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile({ user, setUser }) {
  const navigate = useNavigate();
  const [cashback, setCashback] = useState({ balance: 0, expires_at: null });
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (user) {
      fetchCashback();
      fetchOrders();
    }
  }, [user]);

  const fetchCashback = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/user/cashback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCashback(data);
    } catch (error) {
      console.error('Error fetching cashback:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setLoadingOrders(false); return; }
      const response = await fetch(`${BACKEND_URL}/api/orders/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
    toast.success('Sesión cerrada');
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
        </div>
      </header>

      {/* Notice Banner */}
      <NoticeBanner />

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Playfair Display' }}>Mi Perfil</h1>

        {/* ArtFitz Club card */}
        <div className="mb-6">
          <LoyaltyCard user={user} />
        </div>

        <div className="space-y-6">
          <div className="border-2 border-black p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user.name}</h2>
                <p className="text-gray-600">{user.email}</p>
              </div>
            </div>

            {/* Cashback Section */}
            {cashback.balance > 0 && (
              <div className="bg-green-50 border-2 border-green-500 p-4 rounded mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <Gift className="w-6 h-6 text-green-600" />
                  <h3 className="text-xl font-bold text-green-800">Cashback Disponible</h3>
                </div>
                <p className="text-3xl font-bold text-green-600 mb-1">
                  {cashback.balance.toFixed(2)}€
                </p>
                {cashback.expires_at && (
                  <p className="text-sm text-green-700">
                    Caduca el: {new Date(cashback.expires_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
                <p className="text-xs text-green-600 mt-2">
                  Úsalo en tu próxima compra en el checkout
                </p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-gray-600" data-testid="profile-orders-count">
                <Package className="w-5 h-5" />
                <span>
                  {loadingOrders
                    ? 'Cargando pedidos…'
                    : `${orders.length} pedido${orders.length === 1 ? '' : 's'} realizado${orders.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </div>

            <Button onClick={handleLogout} variant="outline" className="w-full border-2 border-black hover:bg-black hover:text-white py-4">
              CERRAR SESIÓN
            </Button>
          </div>

          {/* Cashback Info Card */}
          <div className="border border-gray-200 p-6 bg-gray-50">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Programa de Cashback
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>✅ Gana <strong>3% de cashback</strong> en todos los productos Arthur Fitz</p>
              <p>✅ Usa tu cashback en tu próxima compra</p>
              <p>✅ El cashback caduca después de 1 año</p>
              <p className="text-xs text-gray-500 mt-3">
                * Solo aplicable a productos de la marca Arthur Fitz
              </p>
            </div>
          </div>

          <div className="border border-gray-200 p-6" data-testid="my-orders-section">
            <h3 className="text-xl font-bold mb-4">Mis Pedidos</h3>
            {loadingOrders ? (
              <p className="text-center py-6 text-gray-500">Cargando pedidos...</p>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 text-gray-600" data-testid="no-orders-empty">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                <p>Aún no has realizado pedidos</p>
                <Link to="/productos" className="text-black hover:underline mt-2 inline-block">Comenzar a comprar</Link>
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-orders-list">
                {orders.map((o) => (
                  <details
                    key={o.id}
                    className="border border-gray-200 hover:border-black transition-colors group"
                    data-testid={`order-row-${o.id.substring(0, 8)}`}
                  >
                    <summary className="p-4 cursor-pointer list-none">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-mono text-sm text-gray-500">#{o.id.substring(0, 8).toUpperCase()}</p>
                          <p className="font-semibold">
                            {new Date(o.created_at).toLocaleDateString('es-ES', {
                              day: '2-digit', month: 'long', year: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">{o.total.toFixed(2)}€</p>
                          <span
                            className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded ${
                              o.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                              o.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                              o.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                              o.order_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {o.order_status || 'pending'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600 flex items-center justify-between">
                        <span>
                          {o.items?.length || 0} artículo{o.items?.length === 1 ? '' : 's'}
                          {' · '}
                          Pago: {o.payment_method === 'stripe' ? 'Tarjeta' : o.payment_method === 'bizum' ? 'Bizum' : o.payment_method === 'transferencia' ? 'Transferencia' : o.payment_method}
                          {o.payment_status && (
                            <span className="ml-2">
                              · {o.payment_status === 'paid' ? '✓ Pagado' : o.payment_status === 'pending' ? 'Pendiente pago' : o.payment_status}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 group-open:hidden">Ver seguimiento ▾</span>
                        <span className="text-xs text-gray-500 hidden group-open:inline">Ocultar ▴</span>
                      </div>
                    </summary>
                    <div className="px-4 pb-4">
                      <OrderTrackingTimeline order={o} />
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
